import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { ArrowLeft, Save, X, Upload, Plus, Loader2 } from "lucide-react";

const CATEGORIES = [
  { id: "sneakers", label: "Sneakers" },
  { id: "wigs", label: "Suzel Luxury Hair" },
  { id: "couture", label: "Habillement" },
  { id: "epicerie", label: "Travel & Market" },
  { id: "patisserie", label: "Suzel Bakery" },
  { id: "printing", label: "Printing & IT" },
];

const empty = {
  name: "",
  brand: "",
  category: "sneakers",
  subcategory: "",
  price: 0,
  currency: "USD",
  description: "",
  story: "",
  images: [],
  sizes: [],
  colors: [],
  stock: 10,
  is_limited: false,
  is_new: false,
  is_published: true,
  on_request: false,
  slug: "",
};

const ProductForm = () => {
  const { authClient } = useAuth();
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return;
    authClient()
      .get(`/api/admin/products`)
      .then((r) => {
        const p = r.data.find((x) => x.id === id);
        if (p) setForm({ ...empty, ...p });
        else setError("Produit introuvable");
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, authClient]);

  const update = (key) => (e) => {
    const val = e?.target?.type === "checkbox" ? e.target.checked : e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: val }));
  };

  const updateList = (key) => (value) => {
    // value is a comma-or-newline separated string
    const arr = value.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    setForm((f) => ({ ...f, [key]: arr }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      stock: parseInt(form.stock, 10) || 0,
    };
    try {
      if (isEdit) {
        await authClient().put(`/api/admin/products/${id}`, payload);
      } else {
        await authClient().post(`/api/admin/products`, payload);
      }
      navigate("/admin/products");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  // ===== Image handling =====
  const compressAndAdd = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const max = 1400;
          let { width, height } = img;
          if (width > max || height > max) {
            const ratio = Math.min(max / width, max / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", 0.82));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFiles = async (files) => {
    const arr = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (arr.length === 0) return;
    const compressed = await Promise.all(arr.map(compressAndAdd));
    setForm((f) => ({ ...f, images: [...f.images, ...compressed] }));
  };

  const addImageUrl = () => {
    const url = window.prompt("Coller l'URL de l'image :");
    if (url) setForm((f) => ({ ...f, images: [...f.images, url.trim()] }));
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  if (loading) {
    return (
      <div className="text-center py-32 text-white/50">
        <Loader2 className="animate-spin mx-auto text-[#C5A059]" />
      </div>
    );
  }

  return (
    <div data-testid="product-form-page" className="max-w-5xl">
      <Link
        to="/admin/products"
        data-testid="back-to-products"
        className="inline-flex items-center gap-2 text-white/60 hover:text-[#C5A059] text-xs font-grotesk uppercase tracking-[0.18em] mb-8"
      >
        <ArrowLeft size={14} /> Retour aux produits
      </Link>

      <div className="flex items-center justify-between mb-10">
        <h1 className="font-display text-4xl text-white">
          {isEdit ? "Modifier" : "Nouveau"} <span className="italic text-[#C5A059]">produit</span>
        </h1>
      </div>

      <form onSubmit={submit} className="space-y-10" data-testid="product-form">
        {/* Basic */}
        <Section title="Informations">
          <Field label="Nom du produit *" testid="form-name">
            <input
              required
              value={form.name}
              onChange={update("name")}
              data-testid="input-name"
              className="form-input"
              placeholder="Ex: Air Jordan 1 'Shadow'"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Marque" testid="form-brand">
              <input value={form.brand} onChange={update("brand")} data-testid="input-brand" className="form-input" placeholder="Ex: Nike" />
            </Field>
            <Field label="Catégorie *" testid="form-category">
              <select required value={form.category} onChange={update("category")} data-testid="input-category" className="form-input">
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id} className="bg-[#0a0a0a]">{c.label}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Sous-catégorie" testid="form-subcategory" hint="Ex: Lifestyle, Running, Lace Front, Vins, Pain...">
              <input value={form.subcategory || ""} onChange={update("subcategory")} data-testid="input-subcategory" className="form-input" />
            </Field>
            <Field label="Slug (URL)" testid="form-slug" hint="Laisser vide pour génération auto.">
              <input value={form.slug || ""} onChange={update("slug")} data-testid="input-slug" className="form-input" placeholder="auto" />
            </Field>
          </div>
        </Section>

        {/* Prix & stock */}
        <Section title="Prix & Stock">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Field label="Prix" testid="form-price">
              <input
                type="number"
                step="0.01"
                value={form.price}
                onChange={update("price")}
                data-testid="input-price"
                disabled={form.on_request}
                className="form-input disabled:opacity-50"
              />
            </Field>
            <Field label="Devise" testid="form-currency">
              <select value={form.currency} onChange={update("currency")} data-testid="input-currency" className="form-input">
                <option value="USD" className="bg-[#0a0a0a]">USD ($)</option>
                <option value="CDF" className="bg-[#0a0a0a]">CDF (FC)</option>
                <option value="EUR" className="bg-[#0a0a0a]">EUR (€)</option>
              </select>
            </Field>
            <Field label="Stock" testid="form-stock">
              <input type="number" value={form.stock} onChange={update("stock")} data-testid="input-stock" className="form-input" />
            </Field>
            <Field label="Sur demande" testid="form-on-request" hint="Devis personnalisé (haute couture).">
              <ToggleSwitch checked={form.on_request} onChange={update("on_request")} testid="toggle-on-request" />
            </Field>
          </div>
        </Section>

        {/* Description */}
        <Section title="Contenu">
          <Field label="Description courte" testid="form-description">
            <textarea
              rows={3}
              value={form.description || ""}
              onChange={update("description")}
              data-testid="input-description"
              className="form-input resize-none"
            />
          </Field>
          <Field label="L'histoire (optionnel)" testid="form-story" hint="Storytelling premium affiché en bas de la fiche.">
            <textarea
              rows={3}
              value={form.story || ""}
              onChange={update("story")}
              data-testid="input-story"
              className="form-input resize-none"
            />
          </Field>
        </Section>

        {/* Images */}
        <Section title="Images">
          <p className="text-xs text-white/50 mb-4 font-grotesk">
            Glissez-déposez ou cliquez pour téléverser. Les images seront automatiquement compressées.
          </p>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleFiles(e.dataTransfer.files);
            }}
            className="border-2 border-dashed border-white/10 hover:border-[#C5A059]/40 transition-colors p-10 text-center cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            data-testid="image-dropzone"
          >
            <Upload size={20} className="mx-auto text-[#C5A059] mb-3" />
            <p className="text-sm text-white">Téléverser une image</p>
            <p className="text-xs text-white/50 mt-1">JPG, PNG, WEBP — multiple OK</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid="image-file-input"
          />

          <button type="button" onClick={addImageUrl} data-testid="add-image-url-button" className="mt-3 text-xs text-[#C5A059] uppercase tracking-[0.18em] font-grotesk link-underline">
            + Ajouter par URL
          </button>

          {form.images.length > 0 && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {form.images.map((img, idx) => (
                <div key={idx} className="relative aspect-square bg-[#050505] border border-white/5 group">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    data-testid={`remove-image-${idx}`}
                    className="absolute top-2 right-2 w-7 h-7 bg-black/80 text-white hover:text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Variants */}
        <Section title="Variantes">
          <Field label="Tailles disponibles" testid="form-sizes" hint="Séparées par des virgules ou retours à la ligne. Ex: 40, 41, 42">
            <textarea
              rows={2}
              defaultValue={form.sizes.join(", ")}
              onBlur={(e) => updateList("sizes")(e.target.value)}
              data-testid="input-sizes"
              className="form-input resize-none"
              placeholder="40, 41, 42, 43"
            />
          </Field>
          <Field label="Couleurs" testid="form-colors" hint="Séparées par des virgules.">
            <textarea
              rows={2}
              defaultValue={form.colors.join(", ")}
              onBlur={(e) => updateList("colors")(e.target.value)}
              data-testid="input-colors"
              className="form-input resize-none"
              placeholder="Noir, Blanc, Crème"
            />
          </Field>
        </Section>

        {/* Flags */}
        <Section title="Affichage">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Field label="Édition limitée" testid="form-limited">
              <ToggleSwitch checked={form.is_limited} onChange={update("is_limited")} testid="toggle-limited" />
            </Field>
            <Field label="Nouveau" testid="form-new">
              <ToggleSwitch checked={form.is_new} onChange={update("is_new")} testid="toggle-new" />
            </Field>
            <Field label="Publié sur le site" testid="form-published">
              <ToggleSwitch checked={form.is_published} onChange={update("is_published")} testid="toggle-published" />
            </Field>
          </div>
        </Section>

        {error && <p data-testid="form-error" className="text-sm text-red-400">{error}</p>}

        <div className="flex items-center gap-4 pt-6 border-t border-white/5">
          <button type="submit" disabled={saving} data-testid="form-submit" className="btn-gold">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Enregistrement...</> : <><Save size={14} /> Enregistrer</>}
          </button>
          <Link to="/admin/products" className="btn-outline">
            Annuler
          </Link>
        </div>
      </form>

      <style>{`
        .form-input {
          width: 100%;
          background: transparent;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          color: white;
          padding: 12px 0;
          outline: none;
          transition: border-color 0.3s;
        }
        .form-input:focus { border-color: #C5A059; }
        select.form-input option { background: #0a0a0a; }
      `}</style>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="bg-[#0a0a0a] border border-white/5 p-8 space-y-6">
    <h2 className="font-display text-2xl text-white mb-2">{title}</h2>
    {children}
  </section>
);

const Field = ({ label, hint, children, testid }) => (
  <div data-testid={testid}>
    <label className="overline block mb-3">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-white/40 mt-2 italic">{hint}</p>}
  </div>
);

const ToggleSwitch = ({ checked, onChange, testid }) => (
  <button
    type="button"
    onClick={() => onChange({ target: { type: "checkbox", checked: !checked } })}
    data-testid={testid}
    className={`relative w-12 h-6 transition-colors ${checked ? "bg-[#C5A059]" : "bg-white/10"}`}
  >
    <span
      className={`absolute top-0.5 w-5 h-5 bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-0.5"}`}
    />
  </button>
);

export default ProductForm;
